import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
  #controller = new AbortController();
  #activeFetch = null;
  #isKeyboardNavigation = false;

  get dialog() {
    return /** @type {DialogComponent | null} */ (this.closest('dialog-component'));
  }

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this;
    const { signal } = this.#controller;
    if (this.refs.searchInput.value.length > 0) this.#showResetButton();
    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
  }

  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => this.#resetSearch(false);
  #handleDialogOpen = () => requestAnimationFrame(() => this.refs.searchInput?.focus());

  handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest?.('button,a,input,select,textarea')) this.refs.searchInput?.focus();
  };

  get #allResultsItems() {
    return Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-products .predictive-search-results__card, .predictive-search-results__list .predictive-search-results__card'
      )
    ).filter((item) => item instanceof HTMLElement);
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;
    items.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.toggleAttribute('aria-selected', active);
      item.classList.toggle('keyboard-focus', active && this.#isKeyboardNavigation);
      if (active) {
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
        });
      }
    });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }
    const items = this.#allResultsItems;
    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
    const current = this.#currentIndex;
    const total = items.length;
    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = current < total - 1 ? current + 1 : 0;
      return;
    }
    if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = current > 0 ? current - 1 : total - 1;
      return;
    }
    if (event.key === 'Enter') {
      const single = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (single instanceof HTMLElement && single.dataset.singleResultUrl) {
        event.preventDefault();
        location.href = single.dataset.singleResultUrl;
      } else if (this.#currentIndex >= 0) {
        event.preventDefault();
        this.#currentItem?.querySelector('a')?.click();
      }
    }
  };

  resetSearch = debounce((keepFocus = true) => this.#resetSearch(keepFocus), 100);
  search = debounce((event) => {
    if (!event.inputType) return;
    const term = this.refs.searchInput.value.trim();
    this.#clearSelection();
    if (!term.length) {
      this.#resetSearch();
      return;
    }
    this.#showResetButton();
    this.#getSearchResults(term);
  }, 180);

  #clearSelection() {
    this.#allResultsItems.forEach((item) => {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    });
    this.#isKeyboardNavigation = false;
  }

  #setBusy(state) {
    this.refs.predictiveSearchResults.setAttribute('aria-busy', String(state));
    this.toggleAttribute('data-loading', state);
  }

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  #showError() {
    this.refs.predictiveSearchResults.innerHTML = `
      <div class="zyma-predictive-error" role="alert">
        <strong>Search results couldn’t load.</strong>
        <span>Try again in a moment.</span>
      </div>`;
  }

  async #getSearchResults(term) {
    if (!this.dataset.sectionId) return;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[type]', 'product,article');
    const controller = this.#createAbortController();
    this.#setBusy(true);
    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!markup || controller.signal.aborted) return;
      morph(this.refs.predictiveSearchResults, markup);
      this.#resetScrollPositions();
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('Predictive search failed', error);
        this.#showError();
      }
    } finally {
      if (!controller.signal.aborted) this.#setBusy(false);
    }
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  async #resetSearch(keepFocus = true) {
    const { predictiveSearchResults, searchInput } = this.refs;
    this.#clearSelection();
    searchInput.value = '';
    this.#hideResetButton();
    const controller = this.#createAbortController();
    const url = new URL(location.href);
    url.searchParams.delete('page');
    this.#setBusy(true);
    try {
      const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      if (!markup || controller.signal.aborted) return;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const next = parsed.querySelector('#predictive-search-results');
      if (!next) return;
      morph(predictiveSearchResults, next);
      this.#resetScrollPositions();
      if (keepFocus) searchInput.focus();
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('Search reset failed', error);
        this.#showError();
      }
    } finally {
      if (!controller.signal.aborted) this.#setBusy(false);
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
